import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { CommentsField } from "@/shared/components/CommentsField";
import { Stand } from "@/shared/models/Stand";
import { AdjustmentReasonSelector } from "@/shared/selectors/AdjustmentReasonSelector";
import { AdjustmentTypeSelector } from "@/shared/selectors/AdjustmentTypeSelector";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import { Box, Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAdjustmentsByShift } from "../AdjustmentManagerPage";
import { MillAdjustmentByShift } from "../models/MillAdjustmentByShift";
import {
	setStandsToMillAdjustment,
	upsertMillAdjustment,
} from "../repositories/AdjustmentManagerRepository";
import { StandField } from "./StandField";
import { useStandsByMillAdjustment } from "./StandsOfMillAdjustmentSelector";

const moment = getMomentTz();

type Props = {
	assetId?: number | null;
	showModal: "new" | "edit" | "delete" | "";
	onHide: (update: boolean) => void;
	millAdjustmentId: number | null;
	initialStands?: Stand[];
	limitSelectableStands?: String[];
	focusComments?: boolean;
	suffixTitle?: string;
};

export const UpsertAdjustmentModal = ({
	assetId,
	showModal,
	onHide,
	millAdjustmentId,
	initialStands = [],
	limitSelectableStands = [],
	focusComments = false,
	suffixTitle = "Delays Manager",
}: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [title, setTitle] = useState("");
	const [millAdjustment, setMillAdjustment] =
		useState<MillAdjustmentByShift | null>(null);
	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);

	const {
		data,
		isLoading,
		isSuccess,
		error: e,
		isError,
	} = useAdjustmentsByShift({
		millAdjustmentId: millAdjustmentId,
	});

	const { data: d } = useStandsByMillAdjustment({
		millAdjustmentId: millAdjustmentId ?? 0,
		onError: (error) => setError(error.message),
		onSuccess: (data) => {
			if (data && data.length > 0) {
				setStandsSelected(data);
			} else {
				setStandsSelected([]);
			}
		},
	});

	const { control, handleSubmit, reset, watch, setValue, getValues, trigger } =
		useForm<MillAdjustmentByShift>({
			defaultValues: {},
			mode: "onChange",
		});

	const timestamp = watch("Timestamp");
	const requiredValue = watch("RequiredValue");
	const requiredReason = watch("RequiredReason");

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertMillAdjustment, {
		meta: { successMessage: "The adjustment was saved successfully" },
		onSuccess: async (id: number) => {
			if (id && standsSelected) {
				const resp = await setStandsToMillAdjustment(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}

			//const formattedDate = moment(timestamp);
			//await getShiftDataFromAPI(formattedDate.toDate(), 0);
			await queryClient.invalidateQueries({ queryKey: ["adjustments"] });

			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<MillAdjustmentByShift> = async (
		adjustment: MillAdjustmentByShift,
	) => {
		setIsSubmitLoading(true);
		if (showModal !== "delete") {
			upsertMutation.mutate({
				millAdjustmentId: adjustment.MillAdjustmentId,
				timestamp: adjustment.Timestamp!,
				adjustmentTypeId: adjustment.AdjustmentTypeId!,
				adjustmentReasonId: adjustment.AdjustmentReasonId!,
				operatorId: adjustment.OperatorId!,
				adjustmentValue: adjustment.AdjustmentValue,
				unit: adjustment.Unit,
				delayId: adjustment.DelayId,
				comments: adjustment.Comments,
			});
		}
	};

	useEffect(() => {
		if (showModal !== "") {
			setIsSubmitLoading(false);
			setTitle(`${showModal.toUpperCase()} MILL ADJUSTMENT`);
			if (millAdjustment && showModal !== "new") {
				reset(millAdjustment);
			} else {
				reset({
					MillAdjustmentId: null,
					Timestamp: new Date(),
					Stands: "",
					AdjustmentTypeId: null,
					AdjustmentReasonId: null,
					OperatorId: null,
					AdjustmentValue: null,
					Unit: null,
					DelayId: null,
					Comments: "",
				});
			}
		} else {
			reset();
			setStandsSelected([]);
		}
	}, [showModal, millAdjustment]);

	useEffect(() => {
		if (isSuccess) {
			if (data && data[0]) {
				setMillAdjustment(data[0]);
			}
		}
	}, [isSuccess, data]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	useEffect(() => {
		if (showModal === "new" && initialStands && initialStands.length > 0) {
			const existingStands = standsSelected || [];
			const combinedStands = [...existingStands, ...initialStands];

			// Properly deduplicate Stand objects based on their ID
			const uniqueStands = combinedStands.filter(
				(stand, index, arr) =>
					arr.findIndex((s) => s.StandId === stand.StandId) === index,
			);

			setStandsSelected(uniqueStands);
		}
	}, [showModal, initialStands]);

	return (
		<>
			{(showModal === "new" || showModal === "edit") && (
				<HelmetDexteel
					title={`${capitalize(showModal)} Adjustment - ${suffixTitle}`}
				/>
			)}
			<MesfModal
				maxWidth="md"
				open={showModal !== "" && showModal !== "delete"}
				handleClose={() => {
					onHide(false);
				}}
				id="upsertDelay"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						{isLoading && <CenteredLazyLoading />}
						{!isLoading && (
							<>
								<Grid2 container spacing={1} style={{ alignItems: "center" }}>
									<Grid2 size={{ md: 4 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Timestamp"
												control={control}
												rules={{
													required: "Timestamp date is required",
													validate: {
														notInFuture: (value) => {
															if (value && value > new Date()) {
																return "Timestamp date cannot be in the future.";
															}
															return true;
														},
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<DateTimePicker
														label="Timestamp"
														value={field.value ? moment(field.value) : null}
														onChange={field.onChange}
														timezone={TimeService.getInstance().getServerTimeZone()}
														format="MM/DD/YYYY HH:mm:ss"
														ampm={false}
														disabled={showModal === "delete"}
														slotProps={{
															textField: {
																size: "small",
																error: !!error,
																helperText: error?.message,
																fullWidth: true,
																required: true,
															},
														}}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 8 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Stands"
												control={control}
												rules={{
													required:
														standsSelected.length == 0
															? "Stands are required"
															: undefined,
												}}
												render={({ fieldState: { error } }) => (
													<StandField
														label="Stands"
														onSelectionChanged={async (
															selectedStands: Stand[],
														) => {
															setStandsSelected(selectedStands);
															await trigger("Stands");
														}}
														standsOfMillAdjustment={standsSelected}
														millAdjustmentId={millAdjustmentId}
														error={error}
														disabled={showModal === "delete"}
														limitSelectableStands={limitSelectableStands}
														description={
															limitSelectableStands
																? "You will only be able to select installed stands"
																: undefined
														}
														required
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentTypeId"
												control={control}
												rules={{
													required: "Type is required",
												}}
												render={({ field, fieldState: { error } }) => (
													<AdjustmentTypeSelector
														label="Type"
														value={field.value}
														onChange={async (
															id,
															requiredValue,
															requiredReason,
															unit,
														) => {
															setValue("AdjustmentTypeId", id);
															setValue("RequiredValue", requiredValue);
															setValue("AdjustmentValue", null);
															setValue("RequiredReason", requiredReason);
															setValue("AdjustmentReasonId", null);
															setValue("Unit", unit);
															if (id) await trigger("AdjustmentTypeId");
														}}
														disabled={showModal === "delete"}
														fieldError={error}
														required
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentReasonId"
												control={control}
												rules={{
													required: requiredReason
														? "Reason is required"
														: false,
												}}
												render={({ field, fieldState: { error } }) => (
													<AdjustmentReasonSelector
														label="Reason"
														value={field.value}
														onChange={async (id: number) => {
															field.onChange(id);
															if (id) await trigger("AdjustmentReasonId");
														}}
														disabled={!requiredReason || showModal === "delete"}
														fieldError={error}
														required={requiredReason}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 6 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="OperatorId"
												rules={{ required: "Operator is required" }}
												control={control}
												render={({ field, fieldState: { error } }) => (
													<OperatorSelector
														assetId={assetId ?? undefined}
														label="Operator"
														value={field.value}
														onChange={async (id: number) => {
															field.onChange(id);
															if (id) await trigger("OperatorId");
														}}
														disabled={showModal === "delete"}
														fieldError={error}
														required
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 3 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="AdjustmentValue"
												rules={{
													required: requiredValue
														? "The value is required"
														: false,
												}}
												control={control}
												render={({ field, fieldState: { error } }) => (
													<TextField
														label="Value"
														value={field.value ?? ""}
														onChange={async (e) => {
															field.onChange(e.target.value);
															if (e.target.value)
																await trigger("AdjustmentValue");
														}}
														variant="outlined"
														fullWidth
														size="small"
														error={!!error}
														helperText={error?.message}
														type="number"
														disabled={!requiredValue || showModal === "delete"}
														required={requiredValue}
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 3 }}>
										<Box style={{ height: 60 }}>
											<Controller
												name="Unit"
												control={control}
												render={({ field }) => (
													<TextField
														label="Unit"
														value={field.value ?? ""}
														variant="outlined"
														fullWidth
														size="small"
														disabled
													/>
												)}
											/>
										</Box>
									</Grid2>
									<Grid2 size={{ md: 12 }}>
										<Controller
											name="Comments"
											control={control}
											render={({ field, fieldState: { error } }) => (
												<CommentsField
													value={field.value}
													onChange={field.onChange}
													fieldError={error}
													getValues={getValues}
													disabled={showModal === "delete"}
													maxLength={500}
													focusComment={focusComments}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									onClick={async () => {
										const isValid = await trigger();
										if (isValid) {
											onSubmit(getValues());
										}
									}}
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
